import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { WidgetRoot, type WidgetOptions } from './WidgetRoot';
import { SearchWidget } from './SearchWidget';
import '../styles.css';

/**
 * Mounting a widget into somebody else's page.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ---------------------------------------------------------------------------
 *
 * No routing, no history, no `document.title`, no scroll management, no global
 * listeners. The widget is a guest on a WordPress page and the page owns all of
 * those. Everything it needs about the world arrives in `options`, and the one
 * thing it does to the document is add a `<style>` element for its theme, which
 * is scoped to its own class and shared between widgets rather than duplicated.
 *
 * `destroy()` exists because WordPress pages get replaced by block editors,
 * page-builder previews and turbo-style navigation, and a widget that cannot be
 * taken down leaks a React root and a fetch in flight every time.
 */

export interface MountedWidget {
  destroy(): void;
  /** Re-render with new options — a page builder changing a block's settings. */
  update(next: Partial<WidgetOptions>): void;
}

/**
 * The search widget, and — with `accommodationId` — the single-accommodation
 * variant for that accommodation's own page.
 *
 * One function rather than two, because a park's search page and an
 * accommodation's page are the same behaviour over a set of a different size.
 * The scoped form drops the filter column and the results list and leaves the
 * price, the availability and the booking outcome for the post it sits in.
 */
/**
 * Warn, once, if the host page is not secure — and carry on regardless.
 *
 * The opposite of what the back office does, on purpose. This widget is
 * embedded in a park's own site, which we do not control: refusing to render on
 * their staging server, or on a site whose certificate somebody has not got
 * round to, would look like our bug and be reported as one.
 *
 * The guest is also not signing in here. There is no session cookie to leak,
 * and a price is public. What suffers is the enquiry form, which is a real
 * concern — hence the warning, which reaches the developer who can fix it
 * without punishing the park's visitors for something they cannot.
 */
let warned = false;
function warnIfInsecure(): void {
  if (warned || typeof window === 'undefined' || window.isSecureContext !== false) return;
  warned = true;
  // eslint-disable-next-line no-console
  console.warn(
    'PMS widget: this page is not a secure context, so anything a visitor types '
    + 'into the enquiry form travels unencrypted. Serve the site over https.',
  );
}

export function mountSearchWidget(
  element: Element,
  options: WidgetOptions = {},
): MountedWidget {
  warnIfInsecure();
  const root: Root = createRoot(element);
  let current = options;

  const draw = () => {
    root.render(
      <StrictMode>
        <WidgetRoot options={current}>
          <SearchWidget
            scopedTo={current.accommodationId}
            kinds={current.kinds}
            renderResults={current.renderResults}
            showControls={current.showControls}
            showFilters={current.showFilters}
            showResults={current.showResults}
            monthNav={current.monthNav}
            initial={{
              arrival: current.initial?.arrival,
              nights: current.initial?.nights,
              party: current.initial?.party,
            }}
          />
        </WidgetRoot>
      </StrictMode>,
    );
  };

  draw();

  return {
    destroy() { root.unmount(); },
    update(next) { current = { ...current, ...next }; draw(); },
  };
}
